/**
 * RoomSelectionForm renders the UI for joining or creating a room.
 *
 * Why: Users need a form to enter a room code to join, or choose to create
 * a new room. This is a pure component that receives all data as props.
 *
 * When to use: Rendered by RoomSelectionFormContainer. Use directly in
 * Storybook stories with mock props.
 */
import { Button } from '@merels/ui'
import { useTheme } from '../../../contexts/ThemeContext'

interface RoomSelectionFormProps {
  roomName: string
  error: string
  onRoomNameChange: (name: string) => void
  onJoin: () => void
  onCreate: () => void
  isSignedIn: boolean
  onSignIn: () => void
  ownedRooms?: Array<{ roomId: string; roomCode: string; displayName: string; imageUrl?: string }>
  isLoadingRooms?: boolean
  onJoinRoomCode?: (code: string) => void
  onDeleteRoomCode?: (code: string) => void
}

export function RoomSelectionForm({
  roomName,
  error,
  onRoomNameChange,
  onJoin,
  onCreate,
  isSignedIn,
  onSignIn,
  ownedRooms = [],
  isLoadingRooms = false,
  onJoinRoomCode,
  onDeleteRoomCode
}: RoomSelectionFormProps) {
  const { getAppColor } = useTheme()
  const appColorShades = getAppColor()

  return (
    <div className="max-w-md w-full text-center">
      <div className="flex justify-center mb-4">
        <svg
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          width="120"
          height="120"
        >
          {/* square frame */}
          <path
            d="M15 15 V85 H85 V15 Z"
            stroke={appColorShades.main}
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />

          {/* three filled corner nodes */}
          <g fill={appColorShades.main}>
            <circle cx="15" cy="15" r="8" />
            <circle cx="15" cy="85" r="8" />
            <circle cx="85" cy="85" r="8" />
          </g>

          {/* large circle: between previous two placements */}
          {/* center (75,25) is inside the square but closer to the top-right */}
          <circle
            cx="75"
            cy="25"
            r="21"
            stroke={appColorShades.main}
            strokeWidth="8"
            fill={appColorShades.main}
          />
        </svg>
      </div>
      <h1
        className="text-6xl font-semibold mb-6 leading-none"
        style={{
          color: appColorShades.main,
          fontFamily: "'MorrisRomanBlack', serif",
        }}
      >
        Merels
      </h1>
      <h2 className="text-base font-bold mb-4 text-primary">Join or Create a Room</h2>
      {error && (
        <div className="bg-danger-bg/20 border-2 border-danger-bg/40 text-danger p-4 rounded-lg mb-4 font-bold">
          <strong>Error:</strong> {error}
        </div>
      )}
      <div className="flex flex-col gap-4 items-center">
        <div className="flex gap-2 items-center">
          <input
            type="text"
            placeholder="Enter Room Code"
            value={roomName}
            onChange={(e) => onRoomNameChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onJoin()}
            className="p-3 border-2 rounded-lg text-base bg-surface text-primary focus:outline-none focus:ring-2 transition-colors"
            style={{
              borderColor: appColorShades.main,
              '--tw-ring-color': appColorShades.main,
            } as React.CSSProperties & { '--tw-ring-color': string }}
            onFocus={(e) => {
              e.target.style.borderColor = appColorShades.main
              e.target.style.boxShadow = `0 0 0 2px ${appColorShades.main}40`
            }}
            onBlur={(e) => {
              e.target.style.borderColor = appColorShades.main
              e.target.style.boxShadow = 'none'
            }}
            autoFocus
          />
          <Button onClick={onJoin}>
            Join Room
          </Button>
        </div>
        {isSignedIn ? (
          <>
            <div className="text-muted">or</div>
            <Button onClick={onCreate} variant="outline">
              Create New Room
            </Button>
          </>
        ) : (
          <div className="text-sm text-muted mt-2">
            Or{' '}
            <button
              onClick={onSignIn}
              className="font-bold underline hover:text-primary transition-colors cursor-pointer focus:outline-none"
              style={{ color: appColorShades.main }}
            >
              sign in
            </button>{' '}
            to create rooms.
          </div>
        )}
      </div>

      {/* User Room Dashboard */}
      {isSignedIn && (
        <div className="mt-8 w-full max-w-xs mx-auto text-left border-t border-muted/10 pt-6">
          <h3 className="text-sm font-bold text-muted mb-3 uppercase tracking-wider text-center">Your Rooms</h3>
          {isLoadingRooms ? (
            <div className="text-sm text-center py-2 text-muted animate-pulse">Loading rooms...</div>
          ) : ownedRooms && ownedRooms.length > 0 ? (
            <div className="flex flex-col gap-2 max-h-48 overflow-y-auto scrollbar-thin">
              {ownedRooms.map((room) => (
                <div key={room.roomId} className="flex gap-2 items-center w-full group">
                  <button
                    onClick={() => onJoinRoomCode?.(room.roomCode)}
                    className="flex-1 flex items-center p-2.5 bg-surface-hover/30 border border-muted/20 hover:border-accent rounded-lg text-sm text-primary font-medium hover:bg-surface-hover transition-all text-left gap-3"
                  >
                    {room.imageUrl ? (
                      <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 border border-muted/10">
                        <img src={room.imageUrl} alt={room.displayName} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded flex-shrink-0 bg-gradient-to-tr from-accent/20 to-primary/10 border border-muted/10 flex items-center justify-center text-muted font-bold">
                        {room.displayName.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-semibold">{room.displayName}</div>
                      <div className="text-[10px] text-muted font-mono uppercase tracking-wider">{room.roomCode}</div>
                    </div>
                  </button>
                  <div className="relative inline-flex items-center justify-center group">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onDeleteRoomCode?.(room.roomCode)
                      }}
                      style={{ borderColor: 'transparent' }}
                      className="p-2 border rounded-lg transition-all text-muted hover:border-danger hover:bg-danger/10 hover:text-danger focus:outline-none flex justify-center items-center cursor-pointer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                        />
                      </svg>
                    </button>
                    <span className="absolute top-[125%] left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-md bg-neutral-900/95 text-white border border-white/10 text-[10px] font-semibold shadow-lg whitespace-nowrap opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all duration-100 pointer-events-none z-50">
                      Delete Room
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 -mb-[1px] border-[4px] border-transparent border-b-neutral-900/95" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-center py-4 bg-muted/5 border border-dashed border-muted/20 rounded-lg text-muted">
              No rooms created yet.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
